import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Calendar, CheckCircle, Clock, Euro, Loader2, 
  FileText, ChevronDown, ChevronRight,
  Bookmark, BookOpen, CheckCheck, PlusCircle, User, Target, 
  Tent, ClipboardList, Trophy, Home, Handshake, FileCheck, Ticket
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category labels for display
const categoryLabels = {
  cours_hebdo_proprio: 'Cours collectifs (tarif préférentiel)',
  cours_collectif_ponctuel: 'Cours collectifs ponctuels',
  cours_inclus_forfait: 'Cours inclus dans le forfait',
  cours_hors_forfait: 'Cours hors forfait',
  cours_particulier: 'Cours particuliers',
  stage_equitation_travail: 'Stages équitation de travail',
  stage_autre: 'Stages',
  stage_demi_journee: 'Stages 1/2 journée',
  stage_journee: 'Stages journée',
  forfait: 'Forfait trimestriel',
  forfait_competition: 'Forfait compétition',
  pension: 'Pension',
  demi_pension: 'Demi-pension',
  licence: 'Licence FFE',
  adhesion: 'Adhésion club'
};

// Category icons as React components
const CategoryIcon = ({ type, className = "w-4 h-4" }) => {
  const icons = {
    cours_hebdo_proprio: <Bookmark className={className} />,
    cours_collectif_ponctuel: <BookOpen className={className} />,
    cours_inclus_forfait: <CheckCheck className={className} />,
    cours_hors_forfait: <PlusCircle className={className} />,
    cours_particulier: <User className={className} />,
    stage_equitation_travail: <Target className={className} />,
    stage_autre: <Tent className={className} />,
    stage_demi_journee: <Tent className={className} />,
    stage_journee: <Tent className={className} />,
    forfait: <ClipboardList className={className} />,
    forfait_competition: <Trophy className={className} />,
    pension: <Home className={className} />,
    demi_pension: <Handshake className={className} />,
    licence: <FileCheck className={className} />,
    adhesion: <Ticket className={className} />
  };
  return icons[type] || <FileText className={className} />;
};

export const CavalierDecompteView = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [trimesters, setTrimesters] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedTrimester, setSelectedTrimester] = useState(1);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Fetch available trimesters
  useEffect(() => {
    const fetchTrimesters = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/decompte/trimestres`);
        setTrimesters(response.data.trimesters || []);
        if (response.data.trimesters?.length > 0) {
          // Find current trimester
          const current = response.data.trimesters.find(t => t.is_current) || response.data.trimesters[0];
          setSelectedYear(current.year);
          setSelectedTrimester(current.trimester);
        }
      } catch (error) {
        console.error('Error fetching trimesters:', error);
      }
    };
    fetchTrimesters();
  }, []);

  // Fetch user's decompte data
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/api/decompte/cavalier/${user.id}?year=${selectedYear}&trimester=${selectedTrimester}`
        );
        setData(response.data);
      } catch (error) {
        console.error('Error fetching decompte:', error);
        toast.error('Erreur lors du chargement du décompte');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id, selectedYear, selectedTrimester]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Get trimester label
  const getTrimesterLabel = (t) => {
    const labels = { 1: 'Janvier - Mars', 2: 'Avril - Juin', 3: 'Juillet - Sept', 4: 'Octobre - Déc' };
    return labels[t] || `T${t}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E4A3D]" />
      </div>
    );
  }

  // Group items by status
  const invoicedItems = [];
  const pendingItems = [];
  
  if (data?.items) {
    Object.entries(data.items).forEach(([category, items]) => {
      items.forEach(item => {
        const itemWithCategory = { ...item, category };
        if (item.status === 'invoiced') {
          invoicedItems.push(itemWithCategory);
        } else if (item.status === 'pending') {
          pendingItems.push(itemWithCategory);
        }
      });
    });
  }

  // Sort by date
  const sortByDate = (a, b) => {
    const dateA = a.details?.date || a.created_at || '';
    const dateB = b.details?.date || b.created_at || '';
    return dateA.localeCompare(dateB);
  };
  
  invoicedItems.sort(sortByDate);
  pendingItems.sort(sortByDate);

  // Calculate totals
  const totalInvoiced = invoicedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalPending = pendingItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#2E4A3D]" />
              Décompte d'activité
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={`${selectedYear}-${selectedTrimester}`} onValueChange={(val) => {
                const [year, trimester] = val.split('-');
                setSelectedYear(parseInt(year));
                setSelectedTrimester(parseInt(trimester));
              }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {trimesters.map((t) => (
                    <SelectItem key={`${t.year}-${t.trimester}`} value={`${t.year}-${t.trimester}`}>
                      T{t.trimester} {t.year} - {getTrimesterLabel(t.trimester)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Facturé</span>
              </div>
              <div className="text-2xl font-bold text-green-800">{formatAmount(totalInvoiced)}</div>
              <div className="text-xs text-green-600">{invoicedItems.length} élément{invoicedItems.length > 1 ? 's' : ''}</div>
            </div>
            
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-center gap-2 text-amber-700 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">En cours de facturation</span>
              </div>
              <div className="text-2xl font-bold text-amber-800">{formatAmount(totalPending)}</div>
              <div className="text-xs text-amber-600">{pendingItems.length} élément{pendingItems.length > 1 ? 's' : ''}</div>
            </div>
            
            <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
              <div className="flex items-center gap-2 text-stone-700 mb-1">
                <Euro className="w-4 h-4" />
                <span className="text-sm font-medium">Total trimestre</span>
              </div>
              <div className="text-2xl font-bold text-stone-800">{formatAmount(totalInvoiced + totalPending)}</div>
              <div className="text-xs text-stone-600">{invoicedItems.length + pendingItems.length} élément{(invoicedItems.length + pendingItems.length) > 1 ? 's' : ''}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoiced items */}
      {invoicedItems.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              Activités facturées
              <Badge className="bg-green-100 text-green-800 ml-2">
                {formatAmount(totalInvoiced)}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoicedItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-stone-500">
                      {item.details?.date ? (
                        format(new Date(item.details.date), 'dd/MM/yyyy', { locale: fr })
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CategoryIcon type={item.category} className="w-4 h-4 text-stone-500" />
                        <span>{item.label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {item.item_type === 'cours_inclus_forfait' ? (
                        <span className="text-green-600">Inclus</span>
                      ) : (
                        formatAmount(item.amount)
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Pending items */}
      {pendingItems.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-700">
              <Clock className="w-5 h-5" />
              En cours de facturation
              <Badge className="bg-amber-100 text-amber-800 ml-2">
                {formatAmount(totalPending)}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-stone-500">
                      {item.details?.date ? (
                        format(new Date(item.details.date), 'dd/MM/yyyy', { locale: fr })
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CategoryIcon type={item.category} className="w-4 h-4 text-stone-500" />
                        <span>{item.label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {item.item_type === 'cours_inclus_forfait' ? (
                        <span className="text-green-600">Inclus</span>
                      ) : (
                        formatAmount(item.amount)
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {invoicedItems.length === 0 && pendingItems.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-stone-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-stone-300" />
              <p className="font-medium">Aucune activité pour ce trimestre</p>
              <p className="text-sm mt-1">Les activités passées apparaîtront ici</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CavalierDecompteView;
